import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { FormGroup, UntypedFormGroup } from '@angular/forms';
import { FilterProfileSettingsComponent } from '@components/filter-profile-settings/filter-profile-settings.component';
import { ProfileSelectorConfig } from '@interfaces/profile';
import { IFilterProfile } from '@interfaces/tankProfile';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { SecurityPolicies } from '@services/auth-variables';
import { FilterProfileService } from '@services/filter-profile.service';
import {
  selectFilterProfile,
} from '@state/selectors/filter-profile.selectors';
import { selectCsoId, selectUserInformation } from '@state/selectors/user-profile.selectors';
import { Subject, combineLatest, of, switchMap, withLatestFrom, take } from 'rxjs';
import { AppState } from 'src/app/app.state';
import { AuthService } from 'src/app/services/auth.service';
import { ColumnConfigs } from 'src/app/shared/Constants/sharedConstants';
import { PanelStates, ProfileSettingsTabDisplayComponent } from '../profile-settings-tab-display/profile-settings-tab-display.component';
import { IDirty } from '@interfaces/idirty.interface';
import { AppDialogService } from '@services/app-dialog.service';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'app-filter-profile-settings',
  templateUrl: './filter-profiles.component.html',
  styleUrls: ['./filter-profiles.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterProfilesComponent implements OnDestroy, IDirty {
  @ViewChild('filterProfileSettings') filterProfileSettings: FilterProfileSettingsComponent;
  @ViewChild('profileSelectorContainer') profileSelectorContainer: ProfileSettingsTabDisplayComponent;

  csoId$ = this.store.select(selectCsoId);
  destroy$: Subject<boolean> = new Subject<boolean>();

  hasFilters = false;
  editingFilter = false;
  editingName = false;

  filterProfileConfig: Partial<ProfileSelectorConfig> = {
    profileIdField: 'filterProfileId',
    profileNameField: 'filterProfileName',
    isPersonalField: 'isPersonal',
    isPrivateField: 'isPrivate',
    ownerField: 'owner',
    organizationNameField: 'organizationName',
    translationPrefix: 'FilterProfile',
  };

  get panelState(): PanelStates {
    return !this.hasFilters
      ? PanelStates.NoFilters
      : this.editingFilter
      ? PanelStates.Edit
      : PanelStates.Filters;
  }

  currentEditingFilterProfile: IFilterProfile;
  currentFilterProfile$ = this.store.select(selectFilterProfile);

  filterProfilesData$ = new BehaviorSubject<IFilterProfile[]>([]);
  loading = false;

  filterProfiles$ = this.filterProfilesData$.asObservable();

  get currentfilterProfileFormGroup(): FormGroup | UntypedFormGroup {
    return this.filterProfileSettings?.filterForm || new FormGroup({});
  }

  isDirty(): boolean {
    return this.filterProfileSettings?.isDirty || false;
  }

  constructor(
    private readonly store: Store<AppState>,
    private readonly filterProfileService: FilterProfileService,
    private readonly cdr: ChangeDetectorRef,
    private readonly authService: AuthService,
    private readonly translate: TranslateService,
    private readonly dialogService: AppDialogService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  onFilterProfileSaved(filterProfile: IFilterProfile) {
    this.currentEditingFilterProfile = filterProfile;
    this.refreshFilterProfilesList();
  }

  onEditingProfile(profile: IFilterProfile) {
    this.setEditingFilterProfile(profile);
  }

  onCreateNewProfile() {
    this.loading = true;
    this.cdr.markForCheck();
    
    this.filterProfileService.createFilterProfile(this.currentEditingFilterProfile, true).subscribe({
      next: () => {
        this.refreshFilterProfilesList();
      },
      error: (err) => {
        this.loading = false;
        this.cdr.markForCheck();
        console.error('Failed to create filter profile', err);
      }
    });
  }

  onApplyProfile(profile: IFilterProfile) {
    this.filterProfileService.setLastSelectedFilterProfile(profile);
    this.setEditingFilterProfile(profile);
  }

  onCopyProfile(profile: IFilterProfile) {
    this.loading = true;
    this.cdr.markForCheck();
    
    this.filterProfileService.copyFilterProfile(profile).pipe(take(1)).subscribe({
      next: (copiedProfile) => {
        this.loading = false;
        this.cdr.markForCheck();

        if (copiedProfile) {
          this.refreshFilterProfilesList();
          if (!this.isDirty()) {
            this.profileSelectorContainer.editProfile(copiedProfile);
          } else {
            this.dialogService.showSuccessMessage(
              this.translate.instant('FilterProfile.SaveBeforeApplyingCopiedProfile'),
            );
          }
        }
      },
      error: (err) => {
        this.loading = false;
        this.cdr.markForCheck();
        console.error('Failed to copy filter profile', err);
      }
    });
  }

  onDeleteProfile(profile: IFilterProfile) {
    this.loading = true;
    this.cdr.markForCheck();
    
    this.filterProfileService.deleteFilterProfile(profile).subscribe({
      next: (deleted) => {
          if (this.currentEditingFilterProfile?.filterProfileId === profile.filterProfileId) {
            this.setEditingFilterProfile(null);
          }
          this.refreshFilterProfilesList();
      },
      error: (err) => {
        this.loading = false;
        this.cdr.markForCheck();
        console.error('Failed to delete filter profile', err);
      }
    });
  }

  onCancel() {
    this.setEditingFilterProfile(null);
  }

  onEditProfileNameClicked() {
    this.editingName = true;
    this.currentfilterProfileFormGroup.controls.filterProfileName.enable({ emitEvent: true });
  }

  onCancelProfileNameClicked() {
    this.editingName = false;
    this.currentfilterProfileFormGroup
      .controls.filterProfileName
      .patchValue(this.currentEditingFilterProfile.filterProfileName, { emitEvent: true });
    this.currentfilterProfileFormGroup.controls.filterProfileName.disable();

    if (this.filterProfileSettings) {
      this.filterProfileSettings.resetValidationState();
    }
  }

  getFilterProfileNameError(): string {
    if (
      !this.filterProfileSettings?.validationState?.filterProfileName?.isValid &&
      this.filterProfileSettings?.validationState?.filterProfileName?.errorMessage
    ) {
      return this.translate.instant(
        this.filterProfileSettings.validationState.filterProfileName.errorMessage
      );
    }
    if (this.currentfilterProfileFormGroup.controls.filterProfileName.hasError('required')) {
      return this.translate.instant('FilterProfile.ProfileNameRequired');
    }
    if (this.currentfilterProfileFormGroup.controls.filterProfileName.hasError('minlength')) {
      return this.translate.instant('FilterProfile.ProfileNameMinLength', {
        minLength: ColumnConfigs.FilterProfileName.MinLength,
      });
    }
    if (this.currentfilterProfileFormGroup.controls.filterProfileName.hasError('maxlength')) {
      return this.translate.instant('FilterProfile.ProfileNameMaxLength', {
        maxLength: ColumnConfigs.FilterProfileName.MaxLength,
      });
    }
    return '';
  }

  setEditingFilterProfile(profile: IFilterProfile | null) {
    this.currentEditingFilterProfile = profile;
    this.editingName = false;
  }

  private refreshFilterProfilesList() {
    this.loadFilterProfiles();
  }

  ngOnInit() {
    this.initializeFilterProfiles();
  }

  initializeFilterProfiles() {
    this.loading = true;
    this.loadFilterProfiles();
  }

  loadFilterProfiles() {
    this.loading = true;
    this.cdr.markForCheck();
    
    this.store.select(selectUserInformation).pipe(
      switchMap(userInfo => {
        return combineLatest([
          this.filterProfileService.getFilterProfiles(userInfo.lastSelectedOrganizationId),
          of(userInfo)
        ]);
      }),
      withLatestFrom(
        this.authService.passesPolicyOf(SecurityPolicies.RequiresManageTankProfile)
      ),
      take(1)
    ).subscribe({
      next: ([[profiles, userInfo], hasManageTankProfile]) => {
        this.hasFilters = profiles && profiles.length > 0;
        const processedProfiles = profiles.map(profile => {
          const isReadOnly =
            profile.isReadOnly ||
            (profile.userId !== userInfo.userId && !profile.isPrivate && !hasManageTankProfile);
          return {
            ...profile,
            isReadOnly,
            isPrivate: profile.isPrivate && !isReadOnly,
          };
        });
        this.filterProfilesData$.next(processedProfiles);
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: err => {
        console.error('Failed to load filter profiles', err);
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }
}
